import { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { MedcardRow } from "src/models/OpenedMedcars";
import { referenceService } from 'src/api/references/referenceService';
import { useTranslate } from "src/locales";

interface MedcardPdfViewerProps {
    selectedRow: MedcardRow | null;
    onLoadingChange?: (loading: boolean) => void;
    refreshKey?: number;
}

export const MedcardPdfViewer = ({ selectedRow, onLoadingChange, refreshKey }: MedcardPdfViewerProps) => {
    const { t } = useTranslate();
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [loadingPdf, setLoadingPdf] = useState(false);
    const [pdfError, setPdfError] = useState<string | null>(null);

    useEffect(() => {
        const fetchPdf = async () => {
            if (!selectedRow) {
                setPdfUrl(null);
                setPdfError(null);
                return;
            }

            setLoadingPdf(true);
            if (onLoadingChange) onLoadingChange(true);
            setPdfUrl(null);
            setPdfError(null);

            try {
                const response = await referenceService.reportGenerator.generate(
                    Number(selectedRow.orId),
                    Number(selectedRow.id)
                );

                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob) + "#navpanes=0";
                setPdfUrl(url);
            } catch (error: any) {
                console.error("Error fetching PDF:", error);
                let errorMessage = null;

                if (error?.response?.data) {
                    const data = error.response.data;
                    if (data instanceof ArrayBuffer) {
                        try {
                            const decodedString = new TextDecoder().decode(data);
                            const json = JSON.parse(decodedString);
                            errorMessage = json.error;
                        } catch (e) {
                            console.error("Error parsing error response:", e);
                        }
                    } else if (data.error) {
                        errorMessage = data.error;
                    }
                }

                setPdfError(errorMessage || t("opened-medcards.error-occurred"));
            } finally {
                setLoadingPdf(false);
                if (onLoadingChange) onLoadingChange(false);
            }
        };

        fetchPdf();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRow?.orId, selectedRow?.id, refreshKey]); // Dependencies: fetch only when IDs change or refreshKey updates

    return (
        <Box sx={{ 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            border: '1px solid #ccc',
            borderRadius: '4px',
            m: 2,
            backgroundColor: '#fff',
            overflow: 'hidden',
            position: 'relative' // relative for absolute positioning of loader
        }}>
            {loadingPdf && (
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10
                }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">{t("loading")}</span>
                    </div>
                    <Typography variant="h6" color="primary" sx={{ mt: 2 }}>
                        {t("opened-medcards.generating-report")}
                    </Typography>
                </Box>
            )}

            {pdfError && !loadingPdf && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="error">
                        {pdfError}
                    </Typography>
                </Box>
            )}

            {!loadingPdf && !pdfError && pdfUrl ? (
                <iframe 
                    src={pdfUrl} 
                    width="100%" 
                    height="100%" 
                    style={{ border: 'none' }} 
                    title="PDF Viewer"
                />
            ) : null}
        </Box>
    );
};
